/**
 * placeholder 文案计算 hook
 * 管理输入框 placeholder 的动态生成和海报模板文案同步
 */
import { useRef, useEffect, useMemo } from 'react';
import { getLocale, useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { DATABRAIN_AGENT_ID } from '../../../config/constant';
import { EAgentMentionCapability, type IAgentData } from '../../../AgentCollection/types';
import { hasAgentMentionCapability } from '../../../AgentCollection/utils';

// 中英文默认文案常量，用于语言切换时判断
export const POSTER_DEFAULT_TEXT_ZH = '风格生成魔法海报，';
export const POSTER_DEFAULT_TEXT_EN = ' style to generate magic poster, ';

interface IUsePlaceholderParams {
  /** 是否正在录音 */
  isRecording: boolean;
  /** 语音识别加载中 */
  speekLoading: boolean;
  /** 语音识别失败 */
  isNoRecognize: boolean;
  /** 是否为海报模式 */
  isPosterMode?: boolean;
  /** 当前模板 */
  template?: string;
  /** 选中的海报模板 */
  selectedPosterTemplate?: any;
  /** 服务端参数 */
  serverParams?: any;
  /** 是否需要隐藏（组织相关） */
  shouldHide: boolean;
  /** 当前 Agent 数据 */
  currentData?: IAgentData;
  /** tagging 节点 */
  taggingNode?: React.ReactElement;
  /** 是否有启动提示语 */
  startupPrompt?: boolean;
  /** 当前选中的 DS 模型 */
  currentSelectedDsModel?: any;
  /** 是否允许 / 触发 Skill */
  allowSlashMention: boolean;
  /** 是否允许 @ 触发知识库 */
  allowKnowledgeMention: boolean;
  /** 是否已选报表/图表 tag */
  hasChartTags: boolean;
  /** 输入值 */
  inputValue: string;
  /** 设置输入值 */
  setInputValue: (value: string) => void;
}

const usePlaceholder = ({
  isRecording,
  speekLoading,
  isNoRecognize,
  isPosterMode,
  template,
  selectedPosterTemplate,
  serverParams,
  shouldHide,
  currentData,
  taggingNode,
  startupPrompt,
  currentSelectedDsModel,
  allowSlashMention,
  allowKnowledgeMention,
  hasChartTags,
  inputValue,
  setInputValue,
}: IUsePlaceholderParams) => {
  const { formatMessage, locale } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  const prevSelectedPosterTemplateRef = useRef(selectedPosterTemplate);

  // 切换海报模板时，仅在从「无模板」首次选中模板时才自动填充初始文案，已有用户输入时不覆盖
  useEffect(() => {
    const prevTemplate = prevSelectedPosterTemplateRef.current;
    prevSelectedPosterTemplateRef.current = selectedPosterTemplate;
    if (selectedPosterTemplate && !prevTemplate && !inputValue.trim()) {
      setInputValue(formatMessage({ id: 'dba_magic_poster_style_generated' }));
    }
  }, [selectedPosterTemplate, inputValue, formatMessage]);

  // 切换语言时，如果当前输入值是旧语言的默认文案，则更新为新语言的默认文案
  useEffect(() => {
    if (!selectedPosterTemplate) return;
    const trimmedValue = inputValue.trim();
    const isOldDefaultText = isEN
      ? trimmedValue === POSTER_DEFAULT_TEXT_ZH.trim()
      : trimmedValue === POSTER_DEFAULT_TEXT_EN.trim();
    if (isOldDefaultText) {
      setInputValue(formatMessage({ id: 'dba_magic_poster_style_generated' }));
    }
  }, [isEN]);

  const placeholderMessage = useMemo(() => {
    // 1. 语音录制相关状态（优先级最高）
    if (isRecording) {
      return formatMessage({ id: speekLoading ? 'dba_voice_reco' : 'dba_voice_listen' });
    }
    if (isNoRecognize) {
      return formatMessage({ id: 'dba_voice_fail' });
    }

    // 2. 魔法海报模式
    if (isPosterMode || template === 'magicPoster') {
      return formatMessage({ id: selectedPosterTemplate ? 'dba_magic_poster_data_placeholder' : 'dba_magic_poster_placeholder' });
    }

    // 3. AI 智能表格模式
    if (serverParams?.function?.name === 'ai_excel') {
      return formatMessage({ id: 'dba_qa_ai_table_ask' });
    }

    // 4. Techland 组织特殊文案
    if (shouldHide && currentData?.agentId !== DATABRAIN_AGENT_ID) {
      return formatMessage({ id: 'dba_qa_techland_ask' });
    }

    const agentSupportsSkill = hasAgentMentionCapability(
      currentData,
      EAgentMentionCapability.SKILL,
    );

    const hasSkill = allowSlashMention && agentSupportsSkill;

    // 5. DS Agent 场景（有 taggingNode）
    if (taggingNode) {
      if (startupPrompt) {
        return currentSelectedDsModel?.startupPrompt || formatMessage({ id: 'dba_ds_qa_ask' });
      }
      if (hasSkill && allowKnowledgeMention) return formatMessage({ id: 'dba_qa_ask_skill' });
      if (hasSkill) return formatMessage({ id: 'dba_qa_ask_skill_only' });
      if (allowKnowledgeMention) return formatMessage({ id: 'dba_qa_ask_knowledge' });
      return formatMessage({ id: 'dba_qa_ask' });
    }

    if ((allowSlashMention && !agentSupportsSkill) && !allowKnowledgeMention) {
      return formatMessage({ id: 'dba_qa_ask_question' });
    }
    if ((hasSkill || allowKnowledgeMention) && hasChartTags) {
      return formatMessage({ id: 'dba_qa_ask_question' });
    }
    if (hasSkill && allowKnowledgeMention) return formatMessage({ id: 'dba_qa_ask_skill' });
    if (hasSkill) return formatMessage({ id: 'dba_qa_ask_skill_only' });
    if (allowKnowledgeMention) return formatMessage({ id: 'dba_qa_ask_knowledge' });
    return formatMessage({ id: 'dba_qa_ask' });
  }, [
    isRecording,
    speekLoading,
    isNoRecognize,
    isPosterMode,
    template,
    selectedPosterTemplate,
    serverParams?.function?.name,
    taggingNode,
    startupPrompt,
    currentSelectedDsModel?.startupPrompt,
    shouldHide,
    currentData?.mentionCapabilities,
    allowSlashMention,
    allowKnowledgeMention,
    hasChartTags,
    getLocale(),
  ]);

  return {
    placeholderMessage,
    isEN,
    POSTER_DEFAULT_TEXT_ZH,
    POSTER_DEFAULT_TEXT_EN,
  };
};

export default usePlaceholder;
